(() => {
  if (window.__muaazonProductShareReady) return;
  window.__muaazonProductShareReady = true;

  if (navigator.share) document.documentElement.classList.add('has-native-share');

  // Compact cards open their panel in the document body so media/card overflow cannot clip it.
  const portalOwners = new WeakMap();
  const panelFor = (root) => root?.__muaazonPortalPanel || root?.querySelector?.('[data-product-share-panel]');
  const shareRoot = (target) => target?.closest?.('[data-product-share]') ||
    portalOwners.get(target?.closest?.('[data-product-share-panel]'));

  const closeShare = (root, restoreFocus = false) => {
    if (!root) return;
    const panel = panelFor(root);
    const trigger = root.querySelector('[data-product-share-trigger]');
    if (panel) {
      panel.hidden = true;
      if (root.__muaazonPortalPanel === panel) {
        root.append(panel);
        panel.classList.remove('product-share__panel--portal');
        portalOwners.delete(panel);
        delete root.__muaazonPortalPanel;
      }
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger?.focus?.();
  };

  const closeOthers = (except) => {
    document.querySelectorAll('[data-product-share]').forEach((root) => {
      if (root !== except) closeShare(root);
    });
  };

  const getShareData = (root) => {
    const title = String(root?.dataset?.shareProductTitle || document.title || 'Muaazon product').trim();
    const absolute = String(root?.dataset?.shareProductUrl || '').trim();
    const path = String(root?.dataset?.shareProductPath || location.pathname || '/').trim();
    const imageRaw = String(root?.dataset?.shareProductImage || '').trim();

    let url;
    try {
      url = new URL(absolute || path, location.origin);
    } catch {
      url = new URL(location.href);
    }

    ['preview_theme_id', 'pb', '_fd', 'view'].forEach((key) => url.searchParams.delete(key));
    if (!['https:', 'http:'].includes(url.protocol)) {
      status(root, 'This item has no valid shareable link.');
      return null;
    }

    const variantRoot = root?.closest?.('[data-variant-root], [data-product-card]');
    const variantInput = variantRoot?.querySelector?.(
      'form[action*="/cart/add"] [name="id"], [data-product-card-variant-id]'
    );
    const selectedSwatch = variantRoot?.querySelector?.('[data-product-card-swatch][data-selected="true"]');
    const variantId = String(variantInput?.value || selectedSwatch?.dataset?.variantId ||
      root?.dataset?.shareVariantId || '').trim();
    if (variantId && !root?.classList?.contains('product-share--page')) url.searchParams.set('variant', variantId);

    // Use the selected variant image instead of the image captured when the page first rendered.
    const currentImage = variantRoot?.querySelector?.(
      '.product-card__image--primary, [data-gallery-slide][data-gallery-selected="true"] img'
    );
    const imageCandidate = String(currentImage?.currentSrc || currentImage?.src || imageRaw).trim();
    let image = '';
    if (imageCandidate) {
      try {
        const parsedImage = new URL(imageCandidate, location.origin);
        if (parsedImage.protocol === 'https:') image = parsedImage.href;
      } catch {}
    }

    return {
      title,
      url: url.href,
      image,
      text: `Check this out on Muaazon: ${title}\n${url.href}`,
      clipboardText: `${title}\n\n${url.href}`
    };
  };

  const nativeShare = async (data) => {
    if (!navigator.share) return false;

    // A complete text payload survives share targets that ignore the separate URL field.
    // Avoid adding URL separately here, which can duplicate the link on some devices.
    const payload = { title: data.title, text: data.text };
    if (navigator.canShare && !navigator.canShare(payload)) return false;
    await navigator.share(payload);
    return true;
  };

  const platformUrl = (platform, data) => {
    const u = encodeURIComponent(data.url);
    const t = encodeURIComponent(data.title);
    const full = encodeURIComponent(data.clipboardText);
    // Facebook caches the canonical page, not each selected variant or theme-preview URL.
    const canonicalProductUrl = data.url.split('?')[0].split('#')[0];

    switch (platform) {
      case 'pinterest':
        // Pinterest's media parameter must be a real public image URL, never a product page.
        if (!data.image || !/^https:\/\//i.test(data.image)) return '';
        return `https://www.pinterest.com/pin/create/button/?url=${u}&media=${encodeURIComponent(data.image)}&description=${t}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalProductUrl)}`;
      case 'reddit':
        return `https://www.reddit.com/submit?url=${u}&title=${t}&type=LINK`;
      case 'x':
        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(data.title)}&url=${u}`;
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalProductUrl)}`;
      case 'whatsapp':
        return `https://wa.me/?text=${full}`;
      case 'telegram':
        return `https://t.me/share/url?url=${u}&text=${encodeURIComponent('Check this out on Muaazon: ' + data.title)}`;
      case 'email':
        return `mailto:?subject=${t}&body=${encodeURIComponent(data.clipboardText)}`;
      default:
        return '';
    }
  };

  const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return;
      } catch {
        // A browser can expose the Clipboard API but deny it in a theme preview.
        // Try the legacy copy path before reporting failure.
      }
    }
    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    if (!copied) throw new Error('Clipboard unavailable');
  };

  const status = (root, message) => {
    const node = panelFor(root)?.querySelector?.('[data-product-share-status]');
    if (!node) return;
    node.textContent = message;
    window.clearTimeout(Number(node.dataset.clearTimer || 0));
    const timer = window.setTimeout(() => { node.textContent = ''; }, 3000);
    node.dataset.clearTimer = String(timer);
  };

  const showPreview = (root) => {
    const panel = panelFor(root);
    const data = getShareData(root);
    if (!data) return null;
    const title = panel?.querySelector?.('[data-share-preview-title]');
    const link = panel?.querySelector?.('[data-share-preview-url]');
    const helper = panel?.querySelector?.('.product-share__header span');
    if (helper && navigator.share) {
      helper.textContent = 'Choose the app in your device share sheet';
    }
    if (title) title.textContent = data.title;
    if (link) {
      link.textContent = data.url;
      link.href = data.url;
    }
    return data;
  };

  const openDestination = (destination) => {
    window.open(destination, '_blank', 'noopener,noreferrer,width=760,height=680');
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest?.('[data-product-share-trigger]');
    if (trigger) {
      const root = trigger.closest('[data-product-share]');
      const panel = panelFor(root);
      if (!root || !panel) return;
      const opening = panel.hidden;
      closeOthers(root);
      if (!opening) { closeShare(root, true); return; }
      if (root.classList.contains('product-share--compact')) {
        root.__muaazonPortalPanel = panel;
        portalOwners.set(panel, root);
        panel.classList.add('product-share__panel--portal');
        document.body.append(panel);
      }
      if (!showPreview(root)) {
        closeShare(root, true);
        return;
      }
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      panel.querySelector('button')?.focus?.();
      return;
    }

    const close = event.target.closest?.('[data-product-share-close]');
    if (close) {
      closeShare(shareRoot(close), true);
      return;
    }

    const platformButton = event.target.closest?.('[data-share-platform]');
    if (platformButton) {
      const root = shareRoot(platformButton);
      if (!root) return;
      const data = showPreview(root);
      if (!data) return;
      const platform = platformButton.dataset.sharePlatform;

      // All named buttons use the same proven native payload as More apps
      // when Web Share is supported. Websites cannot preselect a target app;
      // the customer chooses it in the system sheet. Non-supporting browsers
      // retain their destination-specific web/mailto links below.
      if (navigator.share) {
        const appName = platformButton.textContent?.trim() ||
          (platform === 'x' ? 'X' : platform.charAt(0).toUpperCase() + platform.slice(1));
        status(root, 'Choose ' + appName + ' in your device share sheet.');
        try {
          if (await nativeShare(data)) {
            status(root, 'Share sheet closed.');
          } else {
            status(root, 'Native sharing is unavailable. Use Copy title & link.');
          }
        } catch (error) {
          status(root, error?.name === 'AbortError'
            ? 'Share cancelled.'
            : 'Unable to open the share sheet. Use Copy title & link.');
        }
        return;
      }

      if (platform === 'email') {
        window.location.href = platformUrl(platform, data);
        return;
      }

      // Legacy fallback when the browser does not support Web Share.
      const destination = platformUrl(platform, data);
      if (!destination) {
        if (platform === 'pinterest') status(root, 'Pinterest needs a public product image. Use Copy title & link instead.');
        return;
      }

      // Do not await a clipboard write: the click must retain its popup permission.
      openDestination(destination);
      if (platform === 'linkedin') status(root, 'LinkedIn reads the published page preview. Use Copy title & link if it is blank.');
      if (platform === 'reddit') status(root, 'Reddit opens a link-post draft; choose a community before posting.');
      if (platform === 'facebook') status(root, 'Facebook creates the preview from the published product page. If it is blank, use Copy title & link.');
      if (platform === 'pinterest') status(root, 'Pinterest opens with the product image, title and link. If the app discards them, use Copy title & link.');
      return;
    }

    const actionButton = event.target.closest?.('[data-share-action]');
    if (actionButton) {
      const root = shareRoot(actionButton);
      if (!root) return;
      const data = showPreview(root);
      if (!data) return;

      if (actionButton.dataset.shareAction === 'native') {
        try {
          if (await nativeShare(data)) {
            status(root, 'Share sheet closed.');
            return;
          }
        } catch (error) {
          if (error?.name !== 'AbortError') status(root, 'Sharing was not completed.');
        }
        status(root, 'Use Copy title & link if the share sheet cannot transfer content.');
        return;
      }

      if (actionButton.dataset.shareAction === 'copy') {
        try {
          await copyText(data.clipboardText);
          status(root, 'Product title and link copied.');
        } catch {
          status(root, 'Could not copy the product link.');
        }
        return;
      }
    }

    if (!event.target.closest?.('[data-product-share], [data-product-share-panel]')) closeOthers(null);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = Array.from(document.querySelectorAll('[data-product-share]')).find((root) => {
      const panel = panelFor(root);
      return panel && !panel.hidden;
    });
    if (open) closeShare(open, true);
  });
})();